// Which pipeline runs an agent watched, and how they ended.
//
//   tea api "repos/Origin/origin/actions/tasks?limit=120"
//   curl … /commits/<sha>/status
//
// The target is the WORKFLOW, not the run: run ids are minted per push and mean
// nothing a week later, while `shared-merge-ready.yaml` is a thing a reader knows
// and the same workflow fails repeatedly. A run id is kept as evidence.
//
// The verdict vocabulary is the forge's, not ours — success, failure, cancelled,
// waiting — which makes it platform schema, and the last verdict seen per workflow
// accumulates. Whether the agent then DID something about a failure is what the
// surrounding lenses show; this one says what it was looking at and what it saw.

import { shellStatements } from "../../src/mine/statements.js";

const CI_PATH_RE = /actions\/(?:tasks|runs|jobs|artifacts)|\/commits\/[^/\s"']+\/status|check-runs|workflow_dispatch|\/pipelines\b/;
const WORKFLOW_RE = /\b([\w.-]+\.ya?ml)\b/g;
const VERDICT_RE = /\b(success|failure|failing|cancelled|canceled|skipped|waiting|running|queued|pending|error)\b/gi;

/** Workflow files the call or its result named. */
export function workflowsOf(text) {
  const out = [];
  for (const m of String(text || "").matchAll(WORKFLOW_RE)) {
    const w = m[1];
    // Config files are not workflows; a workflow lives under a workflows dir or is
    // referenced beside a run.
    if (/^(?:docker-compose|compose|values|config|renovate|action)\.ya?ml$/i.test(w)) continue;
    if (!out.includes(w)) out.push(w);
  }
  return out.slice(0, 6);
}

/** The verdicts a result reported, in the forge's own vocabulary. */
export function verdictsOf(output) {
  const counts = {};
  for (const m of String(output || "").matchAll(VERDICT_RE)) {
    const v = m[1].toLowerCase().replace("canceled", "cancelled").replace("failing", "failure");
    counts[v] = (counts[v] || 0) + 1;
  }
  return counts;
}

export function extractCiOps(turns) {
  const ops = [];
  for (const st of shellStatements(turns)) {
    if (!CI_PATH_RE.test(st.text)) continue;
    const out = st.output || "";
    // The workflow may only be named in the RESULT — a task listing is a query
    // about "the pipeline" that comes back naming the pieces.
    const named = workflowsOf(st.text);
    const fromResult = workflowsOf(out);
    const workflows = named.length ? named : fromResult;
    if (!workflows.length) continue;

    const verdicts = verdictsOf(out);
    const worst = verdicts.failure ? "failure" : verdicts.cancelled ? "cancelled"
      : verdicts.waiting || verdicts.queued || verdicts.running || verdicts.pending ? "in flight"
      : verdicts.success ? "success" : null;
    const dispatched = /workflow_dispatch|-X\s*POST|--method\s+POST|\brerun\b|\bcancel\b/i.test(st.text);

    ops.push({
      key: st.key,
      workflows,
      workflow: workflows[0],
      // A run id is evidence: minted per push, meaningless a week later.
      runs: [...String(out).matchAll(/\brun#(\d+)|\bid=(\d+)/g)].map((m) => m[1] || m[2]).slice(0, 6),
      kind: dispatched ? "acted" : "watched",
      verdicts,
      verdict: worst,
      namedInResult: !named.length,
      outcome: !out ? "unrecorded" : worst ? "answered" : "answered",
      output: st.output,
      outputStatus: st.outputStatus,
      ok: st.ok,
      steps: [{ kind: dispatched ? "acted on" : "checked", cmd: st.text.replace(/\s+/g, " ").slice(0, 200) }],
      checks: [{ kind: dispatched ? "acted" : "watched", tool: workflows[0],
        scope: workflows.slice(1, 3), outcome: worst || (out ? "no verdict in the result" : "unrecorded") }],
    });
  }
  return ops;
}

export function describeMissing(op) {
  if (!op.verdict && op.output) return "the result named this workflow but carried no verdict for it";
  switch (op.outputStatus) {
    case "backgrounded": return "ran in the background — the pipeline's answer never entered this trace";
    case "none": return "ran and captured no output";
    default: return null;
  }
}
